import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  SparklesIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  LightBulbIcon,
  ListBulletIcon,
  ArrowPathIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { aiChatService } from '../../services/api';
import GoogleTaskText from './GoogleTaskText';
import NoteReviewPanel from './NoteReviewPanel';

// Helper function to format AI responses (markdown-like to styled HTML)
const formatAIResponse = (text) => {
  if (!text) return text;
  
  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
  
  return paragraphs.map((para, idx) => {
    para = para.trim();
    
    // Check for numbered lists (1. 2. 3.)
    if (/^\d+\.\s/.test(para)) {
      const items = para.split(/\n(?=\d+\.\s)/).map(item => {
        const match = item.match(/^\d+\.\s(.+)/);
        return match ? match[1].trim() : item.trim();
      });
      return (
        <ol key={idx} className="list-decimal list-inside space-y-1.5 my-2 ml-2">
          {items.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed">{formatTextContent(item)}</li>
          ))}
        </ol>
      );
    }
    
    // Check for bullet points (- or *)
    if (/^[-*]\s/.test(para)) {
      const items = para.split(/\n(?=[-*]\s)/).map(item => {
        const match = item.match(/^[-*]\s(.+)/);
        return match ? match[1].trim() : item.trim();
      });
      return (
        <ul key={idx} className="list-disc list-inside space-y-1.5 my-2 ml-2">
          {items.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed">{formatTextContent(item)}</li>
          ))}
        </ul>
      );
    }
    
    // Check for headings (### or ##)
    if (/^###\s/.test(para)) {
      const text = para.replace(/^###\s/, '').trim();
      return (
        <h4 key={idx} className="text-base font-semibold text-gray-900 mt-4 mb-2 first:mt-0">
          {formatTextContent(text)}
        </h4>
      );
    }
    
    if (/^##\s/.test(para)) {
      const text = para.replace(/^##\s/, '').trim();
      return (
        <h3 key={idx} className="text-lg font-semibold text-gray-900 mt-4 mb-2 first:mt-0">
          {formatTextContent(text)}
        </h3>
      );
    }
    
    // Regular paragraph
    return (
      <p key={idx} className="text-sm leading-relaxed my-2">
        {formatTextContent(para)}
      </p>
    );
  });
};

// Format text content (bold, italic, etc.)
const formatTextContent = (text) => {
  if (!text) return text;
  
  // Split by ** for bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, idx) => {
    // Bold text
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    // Italic text
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
      return <em key={idx} className="italic">{part.slice(1, -1)}</em>;
    }
    return <span key={idx}>{part}</span>;
  });
};

const NoteDetailsModal = ({ note, open, onClose, onUpdate }) => {
  const queryClient = useQueryClient();
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const messagesRef = useRef(null);
  const chatInputRef = useRef(null);

  // Fetch note with context
  const { data: noteData, refetch } = useQuery({
    queryKey: ['note', note?.id],
    queryFn: () => api.get(`/notes/${note.id}`).then(res => res.data),
    enabled: !!note?.id && open,
  });

  // Load note context when modal opens
  useEffect(() => {
    if (open && noteData?.ai_analysis?.context) {
      const context = noteData.ai_analysis.context;
      // Load previous chat messages if any
      if (context.chat_history) {
        setChatMessages(context.chat_history);
      }
    } else {
      setChatMessages([]);
    }
  }, [open, noteData]);

  // Scroll only the chat; opening a note must leave its review visible.
  useEffect(() => {
    const messages = messagesRef.current;
    if (messages) messages.scrollTop = messages.scrollHeight;
  }, [chatMessages]);

  // Save context mutation
  const saveContextMutation = useMutation({
    mutationFn: async (context) => {
      const currentAnalysis = noteData?.ai_analysis || {};
      const response = await api.put(`/notes/${note.id}`, {
        ai_analysis: {
          ...currentAnalysis,
          context: {
            ...currentAnalysis.context,
            ...context,
          },
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['note', note.id]);
      queryClient.invalidateQueries(['notes']);
      refetch();
    },
  });

  // Send chat message
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isChatLoading) return;

    const userMessage = {
      type: 'user',
      content: chatInput.trim(),
      timestamp: new Date().toISOString(),
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsChatLoading(true);

    try {
      // Send message to AI with note context
      const response = await aiChatService.sendMessage(chatInput.trim(), {
        note_id: note.id,
        note_title: noteData?.title,
        note_content: noteData?.content,
        note_context: noteData?.ai_analysis?.context,
      });

      const aiMessage = {
        type: 'ai',
        content: response.response || 'AI response received',
        timestamp: new Date().toISOString(),
        actions: response.actions_taken || [],
        recommendations: response.recommendations || [],
      };

      setChatMessages(prev => [...prev, aiMessage]);

      // Save updated chat history to note context with extracted todos and summary
      const updatedHistory = [...chatMessages, userMessage, aiMessage];
      
      // Merge extracted todos and summary from response
      const currentContext = noteData?.ai_analysis?.context || {};
      const currentTodos = currentContext.todos || [];
      const newTodos = response.extracted_todos || [];
      
      // Merge todos - avoid duplicates
      const mergedTodos = [...currentTodos];
      newTodos.forEach(newTodo => {
        if (!mergedTodos.find(t => t.text === newTodo.text)) {
          mergedTodos.push({
            ...newTodo,
            id: newTodo.id || Date.now() + Math.random(),
          });
        }
      });

      // Merge links
      const currentLinks = currentContext.links || [];
      const newLinks = response.extracted_links || [];
      const mergedLinks = [...currentLinks];
      newLinks.forEach(newLink => {
        const linkUrl = typeof newLink === 'string' ? newLink : newLink.url;
        if (!mergedLinks.find(l => {
          const existingUrl = typeof l === 'string' ? l : l.url;
          return existingUrl === linkUrl;
        })) {
          mergedLinks.push(newLink);
        }
      });

      await saveContextToNote(updatedHistory, mergedTodos, response.summary || currentContext.summary, mergedLinks);

    } catch (error) {
      toast.error('Failed to get AI response');
      setChatMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setIsChatLoading(false);
    }
  };

  // Save context to note
  const saveContextToNote = async (chatHistory, todos = null, summary = null, links = null) => {
    setIsSaving(true);
    try {
      const currentContext = noteData?.ai_analysis?.context || {};
      
      // Use provided todos/summary or extract from chat history
      let finalTodos = todos;
      let finalSummary = summary;
      
      if (!finalTodos) {
        finalTodos = currentContext.todos || [];
      }
      
      if (!finalSummary) {
        finalSummary = await generateSummary(chatHistory);
      }
      
      await saveContextMutation.mutateAsync({
        chat_history: chatHistory,
        todos: finalTodos,
        summary: finalSummary,
        links: links || currentContext.links || [],
        last_updated: new Date().toISOString(),
      });

    } catch (error) {
      toast.error('Failed to save context');
    } finally {
      setIsSaving(false);
    }
  };

  // Generate summary from chat history
  const generateSummary = async (chatHistory) => {
    if (chatHistory.length === 0) return '';
    
    try {
      const summaryPrompt = `Summarize the following conversation about this note in 2-3 sentences. Focus on key decisions, action items, and important context:\n\n${chatHistory.map(m => `${m.type}: ${m.content}`).join('\n')}`;
      const response = await aiChatService.sendMessage(summaryPrompt, {});
      return response.response || '';
    } catch (error) {
      const lastFewMessages = chatHistory.slice(-4);
      return lastFewMessages.map(m => m.content).join(' ');
    }
  };

  // Toggle todo completion
  const toggleTodo = async (todoId) => {
    const currentTodos = noteData?.ai_analysis?.context?.todos || [];
    const updatedTodos = currentTodos.map(todo =>
      todo.id === todoId ? { ...todo, completed: !todo.completed } : todo
    );

    await saveContextMutation.mutateAsync({
      todos: updatedTodos,
    });
  };

  // Delete todo
  const deleteTodo = async (todoId) => {
    const currentTodos = noteData?.ai_analysis?.context?.todos || [];
    const updatedTodos = currentTodos.filter(todo => todo.id !== todoId);

    await saveContextMutation.mutateAsync({
      todos: updatedTodos,
    });
  };

  if (!open || !note) return null;

  const context = noteData?.ai_analysis?.context || {};
  const todos = context.todos || [];
  const summary = context.summary || '';

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center">
                  <DocumentTextIcon className="w-6 h-6 mr-2 text-purple-600" />
                  {noteData?.title || 'Untitled Note'}
                </h2>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="w-4 h-4" />
                    <span>Created {format(new Date(noteData?.created_at || note.created_at), 'MMM d, yyyy')}</span>
                  </div>
                  {noteData?.tags && noteData.tags.length > 0 && (
                    <div className="flex items-center space-x-1">
                      <TagIcon className="w-4 h-4" />
                      <span>{noteData.tags.length} tag{noteData.tags.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Note Details & Context */}
              <div className="lg:col-span-1 space-y-6">
                {/* Note Content */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-1" />
                    Note Content
                  </h3>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-white p-3 rounded-lg border border-gray-200">
                    {noteData?.content || note.content}
                  </div>
                </div>

                {/* Tags */}
                {noteData?.tags && noteData.tags.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                      <TagIcon className="w-4 h-4 mr-1" />
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {noteData.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Context Summary */}
                {summary && (
                  <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 rounded-xl p-4 border border-purple-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <div className="p-1.5 bg-purple-100 rounded-lg mr-2">
                        <LightBulbIcon className="w-4 h-4 text-purple-600" />
                      </div>
                      AI Context Summary
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{summary}</p>
                  </div>
                )}

                {/* Todos */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <ListBulletIcon className="w-4 h-4 mr-1" />
                    Note checklist
                  </h3>
                  {todos.length === 0 ? (
                    <p className="text-sm text-gray-500">No checklist items yet. Review suggested commitments in Actions from this note.</p>
                  ) : (
                    <div className="space-y-2">
                      {todos.map((todo) => (
                        <div
                          key={todo.id}
                          className="flex items-start space-x-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
                        >
                          <input
                            type="checkbox"
                            checked={todo.completed || false}
                            onChange={() => toggleTodo(todo.id)}
                            className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className={`flex-1 text-sm ${todo.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {todo.text}
                          </span>
                          <button
                            onClick={() => deleteTodo(todo.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Middle & Right Column - Chat (spans 2 columns) */}
              <div className="lg:col-span-2 space-y-4">
                <NoteReviewPanel key={`review-${note.id}`} noteId={note.id} />
                <GoogleTaskText key={note.id} noteId={note.id} />
                <div className="bg-gradient-to-br from-gray-50 to-purple-50/30 rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col" style={{ height: 'calc(95vh - 180px)' }}>
                  <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-gray-200">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900">AI Assistant</h3>
                      <p className="text-xs text-gray-500">Refine, discuss, and enhance your note</p>
                    </div>
                    {isSaving && (
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <ArrowPathIcon className="w-4 h-4 text-purple-500 animate-spin" />
                        <span>Saving...</span>
                      </div>
                    )}
                  </div>

                  {/* Chat Messages */}
                  <div ref={messagesRef} className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2 custom-scrollbar">
                    {chatMessages.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="inline-flex p-4 bg-purple-100 rounded-full mb-4">
                          <SparklesIcon className="w-10 h-10 text-purple-600" />
                        </div>
                        <p className="text-base font-medium text-gray-700 mb-1">Start a conversation about this note!</p>
                        <p className="text-sm text-gray-500">Refine content, fix grammar, discuss ideas, curate article content...</p>
                      </div>
                    ) : (
                      chatMessages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} items-start space-x-2`}
                        >
                          {msg.type === 'ai' && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mt-1">
                              <SparklesIcon className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                              msg.type === 'user'
                                ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-tr-sm'
                                : 'bg-white text-gray-900 border border-gray-200 rounded-tl-sm'
                            }`}
                          >
                            {msg.type === 'ai' ? (
                              <div className="prose prose-sm max-w-none">
                                {formatAIResponse(msg.content)}
                              </div>
                            ) : (
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                            )}
                          </div>
                          {msg.type === 'user' && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mt-1">
                              <span className="text-xs font-medium text-gray-700">U</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    {isChatLoading && (
                      <div className="flex justify-start items-start space-x-2">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <SparklesIcon className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm">
                          <div className="flex space-x-1.5">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Chat Input */}
                  <div className="flex space-x-3 items-end pt-3 border-t border-gray-200">
                    <div className="flex-1 relative">
                      <textarea
                        ref={chatInputRef}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Refine this note, fix grammar, discuss ideas, curate content..."
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none bg-white shadow-sm"
                        rows={2}
                        disabled={isChatLoading}
                      />
                      <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                        Enter to send, Shift+Enter for new line
                      </div>
                    </div>
                    <button
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim() || isChatLoading}
                      className="p-3 bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:transform-none"
                    >
                      <PaperAirplaneIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default NoteDetailsModal;
